import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from './firebase'; 
import GenEdAssigner from './GenEdAssigner'; // <-- NEW IMPORT

const CaseloadManager = ({ tenantId }) => {
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Individualized Student Data State
  const [studentGoals, setStudentGoals] = useState({}); 
  const [studentVoc, setStudentVoc] = useState({});

  // Input States for Adding Goals
  const [newCat, setNewCat] = useState('');
  const [newGoalInputs, setNewGoalInputs] = useState({});
  const [newObjInputs, setNewObjInputs] = useState({});

  // State for EDITING existing goals
  const [editingGoal, setEditingGoal] = useState({ 
    category: '', 
    oldGoalText: '', 
    newGoalText: '',
    objectives: [] 
  });

  // Input States for Vocational
  const [newSite, setNewSite] = useState('');
  const [newTaskInputs, setNewTaskInputs] = useState({});

  // State for EDITING existing vocational sites
  const [editingVoc, setEditingVoc] = useState({
    oldSite: '',
    newSite: '',
    tasks: []
  });

  // 1. Fetch Students on Load
  useEffect(() => {
    let isMounted = true; 
    const fetchStudents = async () => {
      try {
        if (!auth?.currentUser) return;
        const userRef = doc(db, 'districts', tenantId, 'users', auth.currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) return;
        
        const profile = userSnap.data();
        const studentsRef = collection(db, 'districts', tenantId, 'students');
        let studentQuery;
        
        if (profile?.role === 'Case Manager') {
          studentQuery = query(studentsRef, where("assignedCaseManager", "==", auth.currentUser.uid));
        } else if (profile?.role === 'ECE Recorder') {
          studentQuery = query(studentsRef, where("schoolId", "==", profile.schoolId));
        } else {
          studentQuery = query(studentsRef); 
        }
        
        const studentDocs = await getDocs(studentQuery);
        const loadedStudents = studentDocs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (isMounted) setStudents(loadedStudents.sort((a, b) => (a.name || a.studentName || '').localeCompare(b.name || b.studentName || '')));
      } catch (err) {
        console.error("Error fetching students:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchStudents();
    return () => { isMounted = false; };
  }, [tenantId]);

  // 2. Fetch Individual Student's Data when selected
  useEffect(() => {
    const fetchStudentData = async () => {
      if (!selectedStudentId) {
        setStudentGoals({});
        setStudentVoc({});
        setEditingGoal({ category: '', oldGoalText: '', newGoalText: '', objectives: [] });
        setEditingVoc({ oldSite: '', newSite: '', tasks: [] });
        return;
      }
      try {
        const studentRef = doc(db, 'districts', tenantId, 'students', selectedStudentId);
        const snap = await getDoc(studentRef);
        if (snap.exists()) {
          const data = snap.data();
          setStudentGoals(data.customGoals || {});
          setStudentVoc(data.customVocational || {});
        }
      } catch (error) {
        console.error("Error fetching student profile:", error);
      }
    };
    fetchStudentData();
  }, [selectedStudentId, tenantId]);


  // ==========================================
  // --- IEP GOAL HANDLERS ---
  // ==========================================

  const handleAddCategory = () => {
    if (!newCat.trim()) return;
    setStudentGoals({ ...studentGoals, [newCat]: {} });
    setNewCat('');
  };

  const handleAddGoal = (cat) => {
    const goalText = newGoalInputs[cat];
    if (!goalText || !goalText.trim()) return;
    setStudentGoals({
      ...studentGoals,
      [cat]: { ...studentGoals[cat], [goalText]: [] }
    });
    setNewGoalInputs({ ...newGoalInputs, [cat]: '' });
  };

  const handleAddObjective = (cat, goal) => {
    const objKey = `${cat}-${goal}`;
    const objText = newObjInputs[objKey];
    if (!objText || !objText.trim()) return;
    setStudentGoals({
      ...studentGoals,
      [cat]: {
        ...studentGoals[cat],
        [goal]: [...(studentGoals[cat][goal] || []), objText]
      }
    });
    setNewObjInputs({ ...newObjInputs, [objKey]: '' });
  };

  const handleDeleteGoal = (cat, goalText) => {
    if (!window.confirm("Are you sure you want to remove this goal and its objectives?")) return;
    const updatedGoals = JSON.parse(JSON.stringify(studentGoals));
    delete updatedGoals[cat][goalText];
    if (Object.keys(updatedGoals[cat]).length === 0) delete updatedGoals[cat];
    setStudentGoals(updatedGoals);
  };

  const handleSaveGoalEdit = () => {
    const { category, oldGoalText, newGoalText, objectives } = editingGoal;
    if (!newGoalText.trim()) {
      alert("Goal description cannot be empty.");
      return;
    }
    const updatedGoals = JSON.parse(JSON.stringify(studentGoals));
    const cleanObjectives = objectives.filter(obj => obj.trim() !== '');

    delete updatedGoals[category][oldGoalText];
    updatedGoals[category][newGoalText] = cleanObjectives;

    setStudentGoals(updatedGoals);
    setEditingGoal({ category: '', oldGoalText: '', newGoalText: '', objectives: [] }); 
  };

  const handleEditObjectiveChange = (index, newValue) => {
    const updated = [...editingGoal.objectives];
    updated[index] = newValue;
    setEditingGoal({ ...editingGoal, objectives: updated });
  };


  // ==========================================
  // --- VOCATIONAL HANDLERS ---
  // ==========================================

  const handleAddSite = () => {
    if (!newSite.trim()) return;
    setStudentVoc({ ...studentVoc, [newSite]: [] });
    setNewSite('');
  };

  const handleAddTask = (site) => {
    const taskText = newTaskInputs[site];
    if (!taskText || !taskText.trim()) return;
    setStudentVoc({
      ...studentVoc,
      [site]: [...(studentVoc[site] || []), taskText]
    });
    setNewTaskInputs({ ...newTaskInputs, [site]: '' });
  };

  const handleDeleteSite = (site) => {
    if (!window.confirm(`Are you sure you want to remove ${site} and its tasks?`)) return;
    const updatedVoc = JSON.parse(JSON.stringify(studentVoc));
    delete updatedVoc[site];
    setStudentVoc(updatedVoc);
  };

  const handleSaveVocEdit = () => {
    const { oldSite, newSite, tasks } = editingVoc;
    if (!newSite.trim()) {
      alert("Site name cannot be empty.");
      return;
    }
    const updatedVoc = JSON.parse(JSON.stringify(studentVoc));
    const cleanTasks = tasks.filter(t => t.trim() !== '');

    delete updatedVoc[oldSite];
    updatedVoc[newSite] = cleanTasks;

    setStudentVoc(updatedVoc);
    setEditingVoc({ oldSite: '', newSite: '', tasks: [] });
  };

  const handleEditVocTaskChange = (index, newValue) => {
    const updated = [...editingVoc.tasks];
    updated[index] = newValue;
    setEditingVoc({ ...editingVoc, tasks: updated });
  };


  // ==========================================
  // --- SAVE HANDLER ---
  // ==========================================

  const handleSave = async () => {
    if (!selectedStudentId) return;
    setSaving(true);
    setSaveMessage('Saving to student file...');
    try {
      const studentRef = doc(db, 'districts', tenantId, 'students', selectedStudentId);
      await updateDoc(studentRef, {
        customGoals: studentGoals,
        customVocational: studentVoc
      });
      setSaveMessage('Student Profile Updated Successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error("Error saving student data:", error);
      setSaveMessage('Error saving data.');
    } finally {
      setSaving(false);
    }
  };

  // Find the selected student object to extract their current Gen Ed assignments
  const selectedStudentObj = students.find(s => s.id === selectedStudentId);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'sans-serif' }}>Loading Caseload Manager...</div>;

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px', fontFamily: 'sans-serif' }}>
      
      {/* Header */}
      <div style={{ borderBottom: '2px solid #003366', paddingBottom: '15px', marginBottom: '25px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h2 style={{ margin: 0, color: '#003366', fontSize: '28px' }}>Caseload Manager</h2>
          <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>Individualize IEP Goals & Vocational Sites</p>
        </div>
        {selectedStudentId && (
          <button 
            onClick={handleSave} 
            disabled={saving}
            style={{ padding: '12px 20px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '6px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '15px' }}
          >
            {saving ? 'Saving...' : '💾 Save Student Profile'}
          </button>
        )}
      </div>

      {/* Student Selector & Gen Ed Assigner Wrapper */}
      <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginBottom: '30px', display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
        
        {/* Left Side: Student Dropdown */}
        <div style={{ flex: '1 1 300px' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>Select Student on Caseload</label>
          <select 
            value={selectedStudentId} 
            onChange={(e) => setSelectedStudentId(e.target.value)}
            style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '2px solid #003366', fontSize: '16px', backgroundColor: '#f9f9f9' }}
          >
            <option value="">-- Choose Student --</option>
            {students.map(s => (
              <option key={s.id} value={s.id}>{s.name || s.studentName || s.fullName || 'Unknown Student'}</option>
            ))}
          </select>
          {saveMessage && <div style={{ marginTop: '15px', fontWeight: 'bold', color: saveMessage.includes('Error') ? '#ba0c2f' : '#28a745' }}>{saveMessage}</div>}
        </div>

        {/* Right Side: The New Gen Ed Assigner Module */}
        {selectedStudentId && selectedStudentObj && (
          <div style={{ flex: '1 1 300px' }}>
            <GenEdAssigner 
              tenantId={tenantId} 
              studentId={selectedStudentId} 
              initialAssigned={selectedStudentObj.assignedGenEd || []} 
            />
          </div>
        )}
      </div>

      {selectedStudentId && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
          
          {/* SECTION 1: CUSTOM IEP GOALS */}
          <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <h3 style={{ margin: '0 0 5px 0', color: '#003366', fontSize: '22px' }}>📚 Active IEP Goals</h3>
            <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '13px' }}>Input the specific goals and objectives directly from the student's legal IEP.</p>
            
            <div style={{ display: 'flex', gap: '10px', marginBottom: '25px', backgroundColor: '#f4f7f6', padding: '15px', borderRadius: '8px', border: '1px solid #ddd' }}>
              <input 
                placeholder="New Goal Domain (e.g., Reading, Behavior)" 
                value={newCat} 
                onChange={(e) => setNewCat(e.target.value)} 
                style={{ padding: '10px', flex: 1, borderRadius: '4px', border: '1px solid #ccc', fontSize: '15px' }} 
              />
              <button onClick={handleAddCategory} style={{ padding: '10px 20px', backgroundColor: '#003366', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>+ Add Domain</button>
            </div>

            {Object.entries(studentGoals).map(([cat, goalsObj]) => (
              <div key={cat} style={{ marginBottom: '25px', padding: '20px', backgroundColor: '#fdfdfd', border: '2px solid #e6e6e6', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#003366', fontSize: '18px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>Domain: {cat}</h4>
                
                {Object.entries(goalsObj).map(([goalText, objectives]) => {
                  const isEditing = editingGoal.category === cat && editingGoal.oldGoalText === goalText;

                  return (
                    <div key={goalText} style={{ marginLeft: '15px', padding: '15px', marginBottom: '15px', backgroundColor: isEditing ? '#fff' : '#f9f9f9', borderRadius: '6px', borderLeft: '4px solid #6f42c1', border: isEditing ? '1px solid #6f42c1' : 'none' }}>
                      
                      {/* --- EDIT MODE --- */}
                      {isEditing ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                          <div>
                            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>Main Goal</label>
                            <textarea 
                              value={editingGoal.newGoalText}
                              onChange={(e) => setEditingGoal({ ...editingGoal, newGoalText: e.target.value })}
                              style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
                            />
                          </div>

                          <div style={{ backgroundColor: '#f4f7f6', padding: '10px', borderRadius: '4px' }}>
                            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666', marginBottom: '5px', display: 'block' }}>Objectives (Optional)</label>
                            {editingGoal.objectives.map((obj, i) => (
                              <div key={i} style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
                                <input 
                                  value={obj}
                                  onChange={(e) => handleEditObjectiveChange(i, e.target.value)}
                                  style={{ flex: 1, padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                                />
                                <button onClick={() => setEditingGoal({ ...editingGoal, objectives: editingGoal.objectives.filter((_, idx) => idx !== i) })} style={{ background: 'none', border: 'none', color: '#ba0c2f', cursor: 'pointer', fontWeight: 'bold' }}>X</button>
                              </div>
                            ))}
                            <button onClick={() => setEditingGoal({ ...editingGoal, objectives: [...editingGoal.objectives, ''] })} style={{ background: 'none', border: '1px dashed #6f42c1', color: '#6f42c1', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', marginTop: '5px' }}>+ Add Objective</button>
                          </div>

                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={handleSaveGoalEdit} style={{ backgroundColor: '#28a745', color: '#fff', padding: '6px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save Changes</button>
                            <button onClick={() => setEditingGoal({ category: '', oldGoalText: '', newGoalText: '', objectives: [] })} style={{ backgroundColor: '#6c757d', color: '#fff', padding: '6px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        
                        /* --- VIEW MODE --- */
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h5 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '15px', flex: 1 }}>Goal: {goalText}</h5>
                            <div style={{ display: 'flex', gap: '10px' }}>
                              <button onClick={() => setEditingGoal({ category: cat, oldGoalText: goalText, newGoalText: goalText, objectives: [...objectives] })} style={{ background: 'none', border: 'none', color: '#0056b3', cursor: 'pointer', fontSize: '13px' }}>Edit</button>
                              <button onClick={() => handleDeleteGoal(cat, goalText)} style={{ background: 'none', border: 'none', color: '#ba0c2f', cursor: 'pointer', fontSize: '13px' }}>Delete</button>
                            </div>
                          </div>
                          
                          <div style={{ marginLeft: '15px', marginBottom: '15px' }}>
                            {objectives.length === 0 ? (
                              <div style={{ color: '#008000', fontSize: '13px', fontWeight: 'bold' }}>Tracking Primary Goal only (No specific objectives).</div>
                            ) : (
                              objectives.map((obj, i) => (
                                <div key={i} style={{ fontSize: '14px', marginBottom: '6px', color: '#555', display: 'flex' }}>
                                  <span style={{ color: '#6f42c1', marginRight: '8px', fontWeight: 'bold' }}>↳</span> {obj}
                                </div>
                              ))
                            )}
                          </div>

                          <div style={{ display: 'flex', gap: '10px', marginLeft: '15px' }}>
                            <input 
                              placeholder="Add specific objective (Optional)..." 
                              value={newObjInputs[`${cat}-${goalText}`] || ''} 
                              onChange={(e) => setNewObjInputs({ ...newObjInputs, [`${cat}-${goalText}`]: e.target.value })} 
                              style={{ padding: '8px', flex: 1, borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }} 
                            />
                            <button onClick={() => handleAddObjective(cat, goalText)} style={{ padding: '8px 15px', backgroundColor: '#666', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Add Objective</button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}

                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                  <input 
                    placeholder={`Add new overarching goal to ${cat}...`} 
                    value={newGoalInputs[cat] || ''} 
                    onChange={(e) => setNewGoalInputs({ ...newGoalInputs, [cat]: e.target.value })} 
                    style={{ padding: '10px', flex: 1, borderRadius: '4px', border: '1px solid #6f42c1', fontSize: '14px' }} 
                  />
                  <button onClick={() => handleAddGoal(cat)} style={{ padding: '10px 15px', backgroundColor: '#6f42c1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}>Add Goal</button>
                </div>
              </div>
            ))}
          </div>

          {/* SECTION 2: CUSTOM VOCATIONAL SITES */}
          <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <h3 style={{ margin: '0 0 5px 0', color: '#ba0c2f', fontSize: '22px' }}>🏢 Active Job Placements</h3>
            <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '13px' }}>Define the specific work environments and discrete tasks for this student.</p>
            
            <div style={{ display: 'flex', gap: '10px', marginBottom: '25px', backgroundColor: '#fdf4f5', padding: '15px', borderRadius: '8px', border: '1px solid #f5c6cb' }}>
              <input 
                placeholder="New Job Site (e.g., Cardinal Kroger, CK Prep)" 
                value={newSite} 
                onChange={(e) => setNewSite(e.target.value)} 
                style={{ padding: '10px', flex: 1, borderRadius: '4px', border: '1px solid #ccc', fontSize: '15px' }} 
              />
              <button onClick={handleAddSite} style={{ padding: '10px 20px', backgroundColor: '#ba0c2f', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>+ Add Site</button>
            </div>

            {Object.entries(studentVoc).map(([site, tasks]) => {
              const isEditing = editingVoc.oldSite === site;

              return (
                <div key={site} style={{ marginBottom: '25px', padding: '20px', backgroundColor: isEditing ? '#fff' : '#fcfcfc', border: '2px solid', borderColor: isEditing ? '#ba0c2f' : '#eee', borderRadius: '8px' }}>
                  
                  {isEditing ? (
                    /* --- EDIT MODE --- */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>Job Site Name</label>
                        <input 
                          value={editingVoc.newSite}
                          onChange={(e) => setEditingVoc({ ...editingVoc, newSite: e.target.value })}
                          style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
                        />
                      </div>

                      <div style={{ backgroundColor: '#fdf4f5', padding: '10px', borderRadius: '4px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666', marginBottom: '5px', display: 'block' }}>Assigned Tasks</label>
                        {editingVoc.tasks.map((task, i) => (
                          <div key={i} style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
                            <input 
                              value={task}
                              onChange={(e) => handleEditVocTaskChange(i, e.target.value)}
                              style={{ flex: 1, padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                            />
                            <button onClick={() => setEditingVoc({ ...editingVoc, tasks: editingVoc.tasks.filter((_, idx) => idx !== i) })} style={{ background: 'none', border: 'none', color: '#ba0c2f', cursor: 'pointer', fontWeight: 'bold' }}>X</button>
                          </div>
                        ))}
                        <button onClick={() => setEditingVoc({ ...editingVoc, tasks: [...editingVoc.tasks, ''] })} style={{ background: 'none', border: '1px dashed #ba0c2f', color: '#ba0c2f', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', marginTop: '5px' }}>+ Add Task</button>
                      </div>

                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button onClick={handleSaveVocEdit} style={{ backgroundColor: '#28a745', color: '#fff', padding: '6px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save Changes</button>
                        <button onClick={() => setEditingVoc({ oldSite: '', newSite: '', tasks: [] })} style={{ backgroundColor: '#6c757d', color: '#fff', padding: '6px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    
                    /* --- VIEW MODE --- */
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <h4 style={{ margin: '0 0 15px 0', color: '#ba0c2f', fontSize: '18px', borderBottom: '1px solid #eee', paddingBottom: '10px', flex: 1 }}>{site}</h4>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button onClick={() => setEditingVoc({ oldSite: site, newSite: site, tasks: [...tasks] })} style={{ background: 'none', border: 'none', color: '#0056b3', cursor: 'pointer', fontSize: '13px' }}>Edit</button>
                          <button onClick={() => handleDeleteSite(site)} style={{ background: 'none', border: 'none', color: '#ba0c2f', cursor: 'pointer', fontSize: '13px' }}>Delete</button>
                        </div>
                      </div>
                      
                      <div style={{ marginLeft: '15px', marginBottom: '15px' }}>
                        {tasks.length === 0 ? (
                          <div style={{ color: '#999', fontSize: '13px', fontStyle: 'italic' }}>No tasks assigned.</div>
                        ) : (
                          tasks.map((task, i) => (
                            <div key={i} style={{ fontSize: '14px', marginBottom: '6px', color: '#555', display: 'flex' }}>
                               <span style={{ color: '#ba0c2f', marginRight: '8px', fontWeight: 'bold' }}>•</span> {task}
                            </div>
                          ))
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: '10px', marginLeft: '15px' }}>
                        <input 
                          placeholder="Add specific job task (e.g., Stocking Shelves)..." 
                          value={newTaskInputs[site] || ''} 
                          onChange={(e) => setNewTaskInputs({ ...newTaskInputs, [site]: e.target.value })} 
                          style={{ padding: '10px', flex: 1, borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px' }} 
                        />
                        <button onClick={() => handleAddTask(site)} style={{ padding: '10px 15px', backgroundColor: '#ba0c2f', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}>Add Task</button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

        </div>
      )}
    </div>
  );
};

export default CaseloadManager;